import { useState, useEffect, useCallback } from 'react';

import { useUI } from '@/hooks/ui/useUI';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useTranslation } from '@/i18n';
import { recalculationMonthlyCutsService } from '@/api/domains/recalculation';


export interface UseCutRecalculationStatusResult {
  isCutRecalculating: boolean;
  isCheckingCutStatus: boolean;
}

/**
 * Consulta si el corte mensual seleccionado está en recálculo. A diferencia de
 * `useRecalculationStatus`, NO hace polling: solo consulta al cambiar `monthlyCutId`.
 */
export function useCutRecalculationStatus(
  monthlyCutId: number | undefined,
): UseCutRecalculationStatusResult {
  const { t: translate } = useTranslation('payment');
  const { showApiError } = useUI();
  const { createSignal } = useAbortController();
  const [isCutRecalculating, setIsCutRecalculating] = useState(false);
  const [isCheckingCutStatus, setIsCheckingCutStatus] = useState(false);

  const fetchCutStatus = useCallback(async (currentMonthlyCutId: number) => {
    setIsCheckingCutStatus(true);
    const signal = createSignal();
    const response = await recalculationMonthlyCutsService.getCutProcessStatus(
      currentMonthlyCutId,
      signal,
    );

    if (response.error?.code === 'ABORTED') {
      setIsCheckingCutStatus(false);
      return;
    }

    if (!response.success || !response.data) {
      showApiError(response, translate('list.errors.cutStatusLoadFailed'));
      setIsCutRecalculating(false);
      setIsCheckingCutStatus(false);
      return;
    }

    setIsCutRecalculating(response.data.isRecalculating);
    setIsCheckingCutStatus(false);
  }, [createSignal, showApiError, translate]);

  useEffect(() => {
    if (monthlyCutId === undefined) {
      setIsCutRecalculating(false);
      return;
    }
    void fetchCutStatus(monthlyCutId);
  }, [monthlyCutId, fetchCutStatus]);

  return { isCutRecalculating, isCheckingCutStatus };
}

export default useCutRecalculationStatus;
